import { useTranslation } from "react-i18next";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";
import { Label } from "~/atoms/label";
import { formatDate } from "~/helpers/date";
import { getReviewPeriodLabel } from "~/helpers/document";
import { TagsValue } from "~/molecules/Tags";
import DocumentLinks from "~/molecules/DocumentLinks";
import { LegalEntityDisplayWrapper } from "~/helpers/legalEntities";
import { type DocumentDetails } from "~/api/types";

const InfoField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <Label variant="muted">{label}</Label>
    <div className="text-sm font-medium">
      {children ?? <span className="text-muted-foreground">-</span>}
    </div>
  </div>
);

export const MetadataSection = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="bg-muted/80 flex flex-col gap-2 rounded-md p-2">
    <div className="flex items-center justify-between">
      <p className="text-sm font-medium">{title}</p>
      {action}
    </div>
    {children}
  </div>
);

const ManagersSection = ({ document }: { document: DocumentDetails }) => {
  const { t } = useTranslation();
  return (
    <MetadataSection title={t("commons.managers.title")}>
      <div className="grid grid-cols-2">
        <InfoField label={t("commons.document.owner")}>
          {document.owner.firstName} {document.owner.lastName}
        </InfoField>
        <InfoField label={t("commons.document.publisher")}>
          {document.publisher
            ? `${document.publisher.firstName} ${document.publisher.lastName}`
            : null}
        </InfoField>
      </div>
    </MetadataSection>
  );
};

const ClassificationSection = ({ document }: { document: DocumentDetails }) => {
  const { t } = useTranslation();
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);
  return (
    <MetadataSection title={t("commons.classification.title")}>
      <div className="grid grid-cols-2">
        <InfoField label={t("commons.document.category")}>
          {document.category?.title || null}
        </InfoField>
        <InfoField label={t("commons.document.tier")}>
          {document.tier?.title || null}
        </InfoField>
      </div>
      {isRefNumEnabled && (
        <InfoField label={t("commons.document.referenceNumber")}>
          {document.referenceNumber ?? null}
        </InfoField>
      )}
    </MetadataSection>
  );
};

const ReviewDatesSection = ({ document }: { document: DocumentDetails }) => {
  const { t } = useTranslation();
  return (
    <MetadataSection title={t("commons.document.reviewDates")}>
      <div className="grid grid-cols-2">
        <InfoField label={t("commons.document.startReview")}>
          {formatDate(document.scheduleReviewFrom)}
        </InfoField>
        <InfoField label={t("commons.workflow.endDate")}>
          {formatDate(document.scheduleReviewTo)}
        </InfoField>
      </div>
      <InfoField label={t("modal.reviewSchedule.recurringPeriod")}>
        {getReviewPeriodLabel(document.scheduleReviewPeriod, t)}
      </InfoField>
    </MetadataSection>
  );
};

const TagsSection = ({ document }: { document: DocumentDetails }) => {
  const { t } = useTranslation();
  return (
    <MetadataSection title={t("commons.document.tags")}>
      {document.tags.length === 0 ? (
        <span className="text-muted-foreground">-</span>
      ) : (
        <div className="flex flex-wrap gap-1 pt-2">
          {document.tags.map((tag, index) => (
            <TagsValue key={`${tag.title}-${index}`}>{tag.title}</TagsValue>
          ))}
        </div>
      )}
      <LegalEntityDisplayWrapper documentId={document.id} />
    </MetadataSection>
  );
};

const LinksSection = ({ document }: { document: DocumentDetails }) => {
  const { t } = useTranslation();
  return (
    <MetadataSection title={t("commons.document.links")}>
      <DocumentLinks
        tierId={document.tier.id}
        parentDocuments={document.parentDocuments}
        childDocuments={document.childDocuments}
      />
    </MetadataSection>
  );
};

interface DocumentMetadataViewProps {
  document: DocumentDetails;
}

export const DocumentMetadataView = ({
  document,
}: DocumentMetadataViewProps) => (
  <>
    <ManagersSection document={document} />
    <ClassificationSection document={document} />
    <ReviewDatesSection document={document} />
    <TagsSection document={document} />
    <LinksSection document={document} />
  </>
);
